'use client';

import { useActiveOrganization } from '@/features/organization/api/get-active-organization/get-active-organization.hook';
import {
  type DepositFormValues,
  organizationToServiceDepositValues,
  paymentTermsToDepositValues,
} from '@/features/services-dashboard/service-form';
import { useOrgCurrency } from '@/hooks/use-org-currency';
import { centsToMajorString } from '@/lib/org-currency';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type PackageWithItems,
  useCreatePackage,
  useSyncPackageItems,
  useUpdatePackage,
} from '../api';
import {
  type PackageFormValues,
  buildPackagePayload,
  buildUpdatePackagePayload,
  diffPackageItems,
  packageFormDefaults,
} from './package.form';

type Organization = Parameters<typeof organizationToServiceDepositValues>[0];

/**
 * The payment terms a saved package shows.
 *
 * A package that FOLLOWS the clinic default (`paymentPolicy` null — every
 * website-imported one) shows the policy that default actually charges, not a
 * switched-off deposit. Its own amount/basis/percent stay blank, i.e. "the
 * clinic's", so saving an unrelated edit sends the same effective terms back
 * and is not mistaken for a new deposit the server must check against Stripe.
 */
function packageDepositValues(
  pkg: PackageWithItems,
  organization: Organization
): DepositFormValues {
  if (pkg.paymentPolicy != null) return paymentTermsToDepositValues(pkg);
  const inherited = organization?.defaultPaymentPolicy ?? 'in_clinic';
  return paymentTermsToDepositValues({
    ...pkg,
    paymentPolicy: inherited,
    requiresDeposit: inherited === 'deposit',
  });
}

export function packageFormFromPackage(
  pkg: PackageWithItems,
  organization: Organization
): PackageFormValues {
  return {
    ...packageFormDefaults,
    name: pkg.name,
    description: pkg.description ?? '',
    items: [...pkg.items]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({ serviceId: item.serviceId, quantity: item.quantity })),
    priceRaw: centsToMajorString(pkg.priceCents),
    validityDays: pkg.validityDays != null ? String(pkg.validityDays) : '',
    sellOnline: pkg.sellOnline,
    locationIds: pkg.locationIds,
    ...packageDepositValues(pkg, organization),
  };
}

const DEPOSIT_KEYS = [
  'requiresDeposit',
  'depositAmount',
  'depositBasis',
  'depositPercent',
] as const;

type Items = PackageFormValues['items'];

export function usePackageForm({
  pkg,
  onSuccess,
}: {
  /** Null → create mode. */
  pkg: PackageWithItems | null;
  onSuccess?: () => void;
}) {
  const isEdit = !!pkg;
  const { currency, format } = useOrgCurrency();
  const { data: organization } = useActiveOrganization();

  /**
   * What the form shows before the user touches it, DERIVED every render rather
   * than seeded by an effect: the saved package in edit mode, the clinic's
   * payment defaults in create mode. Either may arrive after mount (the list
   * query, the organization), and deriving means a late arrival simply shows
   * up, with no effect racing the first keystroke.
   */
  const base = useMemo<PackageFormValues>(
    () =>
      pkg
        ? packageFormFromPackage(pkg, organization ?? null)
        : {
            ...packageFormDefaults,
            ...organizationToServiceDepositValues(organization ?? null),
          },
    [pkg, organization]
  );
  const baseRef = useRef(base);
  baseRef.current = base;

  /** Only what the user changed. It always wins, so a refetch cannot clobber it. */
  const [edits, setEdits] = useState<Partial<PackageFormValues>>({});
  const values = useMemo<PackageFormValues>(
    () => ({ ...base, ...edits }),
    [base, edits]
  );

  const { createPackageAsync, isCreating } = useCreatePackage();
  const { updatePackageAsync, isUpdating } = useUpdatePackage();
  const { syncPackageItemsAsync, isSyncing } = useSyncPackageItems();
  const isSaving = isCreating || isUpdating || isSyncing;

  // Mirrors the service form: touching a deposit field drops a preserved
  // pay-in-full policy, and the switch sets the policy outright.
  const patch = useCallback((next: Partial<PackageFormValues>) => {
    const changesDeposit = DEPOSIT_KEYS.some((key) => key in next);
    setEdits((prev) => ({
      ...prev,
      ...next,
      ...(changesDeposit ? { paymentPolicy: undefined } : {}),
    }));
  }, []);

  const setRequiresDeposit = useCallback((requiresDeposit: boolean) => {
    setEdits((prev) => ({
      ...prev,
      requiresDeposit,
      paymentPolicy: requiresDeposit ? 'deposit' : 'in_clinic',
    }));
  }, []);

  /** Edit the rows, starting from the saved ones until the user has touched them. */
  const updateItems = useCallback(
    (change: (items: Items) => Items) =>
      setEdits((prev) => ({
        ...prev,
        items: change(prev.items ?? baseRef.current.items),
      })),
    []
  );

  const setItem = useCallback(
    (index: number, next: Partial<Items[number]>) =>
      updateItems((items) =>
        items.map((item, i) => (i === index ? { ...item, ...next } : item))
      ),
    [updateItems]
  );

  const addItem = useCallback(
    () => updateItems((items) => [...items, { serviceId: '', quantity: 1 }]),
    [updateItems]
  );

  const removeItem = useCallback(
    (index: number) =>
      updateItems((items) =>
        items.length > 1 ? items.filter((_, i) => i !== index) : items
      ),
    [updateItems]
  );

  const submit = useCallback(async () => {
    let body: ReturnType<typeof buildPackagePayload>;
    try {
      body = buildPackagePayload(values);
    } catch (error) {
      // The builder's messages ARE the operator-facing ones.
      toast.error(
        error instanceof Error ? error.message : 'Check the form and try again'
      );
      return;
    }

    try {
      if (isEdit && pkg) {
        // The package's own fields first: a refused payment change (Stripe)
        // stops the save before any service is touched.
        await updatePackageAsync({
          packageId: pkg.id,
          ...buildUpdatePackagePayload(values),
        });
        await syncPackageItemsAsync({
          packageId: pkg.id,
          changes: diffPackageItems(pkg.items, values),
        });
        toast.success('Package updated');
      } else {
        await createPackageAsync(body);
      }
      onSuccess?.();
    } catch {
      // The hooks already show the error toast.
    }
  }, [
    values,
    isEdit,
    pkg,
    createPackageAsync,
    updatePackageAsync,
    syncPackageItemsAsync,
    onSuccess,
  ]);

  return {
    isEdit,
    values,
    patch,
    setRequiresDeposit,
    setItem,
    addItem,
    removeItem,
    isSaving,
    submit,
    currencySymbol: currency.symbol,
    format,
  };
}
